<script lang="ts">
	import { onNavigate } from '$app/navigation';
	import { browser } from '$app/environment';

	let { children } = $props();

	// Detect mobile for skipping expensive view transitions
	const isMobile =
		browser &&
		/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

	// Enable view transitions API for supported browsers (desktop only)
	onNavigate((navigation) => {
		// Skip view transitions on mobile - they block navigation for several seconds
		// while capturing DOM screenshots
		if (!document.startViewTransition || isMobile) return;

		// Determine direction based on URL depth
		const fromPath = navigation.from?.url?.pathname || '';
		const toPath = navigation.to?.url?.pathname || '';
		const fromDepth = fromPath.split('/').filter(Boolean).length;
		const toDepth = toPath.split('/').filter(Boolean).length;
		const isForward = toDepth >= fromDepth;

		// Set direction attribute for CSS to use
		document.documentElement.dataset.transitionDir = isForward ? 'forward' : 'back';

		return new Promise((resolve) => {
			document.startViewTransition(async () => {
				resolve();
				await navigation.complete;
			});
		});
	});
</script>

<div class="page-transition">
	{@render children()}
</div>

<style>
	.page-transition {
		view-transition-name: page;
	}

	/* Forward navigation */
	:global([data-transition-dir='forward'] ::view-transition-old(page)) {
		animation: page-fade-out-forward 400ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
	}

	:global([data-transition-dir='forward'] ::view-transition-new(page)) {
		animation: page-fade-in-forward 500ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
	}

	/* Back navigation */
	:global([data-transition-dir='back'] ::view-transition-old(page)) {
		animation: page-fade-out-back 400ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
	}

	:global([data-transition-dir='back'] ::view-transition-new(page)) {
		animation: page-fade-in-back 500ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
	}

	/* Default fallback */
	:global(::view-transition-old(page)) {
		animation: page-fade-out-forward 400ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
	}

	:global(::view-transition-new(page)) {
		animation: page-fade-in-forward 500ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
	}

	@keyframes page-fade-out-forward {
		0% {
			opacity: 1;
			filter: blur(0);
			transform: translateY(0) scale(1);
		}
		100% {
			opacity: 0;
			filter: blur(12px);
			transform: translateY(-20px) scale(0.98);
		}
	}

	@keyframes page-fade-in-forward {
		0% {
			opacity: 0;
			filter: blur(12px);
			transform: translateY(30px) scale(0.98);
		}
		100% {
			opacity: 1;
			filter: blur(0);
			transform: translateY(0) scale(1);
		}
	}

	@keyframes page-fade-out-back {
		0% {
			opacity: 1;
			filter: blur(0);
			transform: translateY(0) scale(1);
		}
		100% {
			opacity: 0;
			filter: blur(12px);
			transform: translateY(20px) scale(0.98);
		}
	}

	@keyframes page-fade-in-back {
		0% {
			opacity: 0;
			filter: blur(12px);
			transform: translateY(-30px) scale(0.98);
		}
		100% {
			opacity: 1;
			filter: blur(0);
			transform: translateY(0) scale(1);
		}
	}

	/* Note: Mobile skips view transitions entirely (in JS) for instant navigation */
</style>
